import React, { useEffect, useState, useCallback } from 'react';
import { Table, Tag, message, Select, Input, DatePicker, Button, Space, Switch, Modal } from 'antd';
import { ReloadOutlined, CheckCircleOutlined, SendOutlined } from '@ant-design/icons';
import { CommentType, RuleType } from '../types';
import { apiFetch, formatDateTime } from '../api';

const statusLabels: Record<string, { color: string; label: string }> = {
  new: { color: 'blue', label: 'Новый' },
  processed: { color: 'orange', label: 'Обработан' },
  published: { color: 'green', label: 'Опубликован' },
  ignored: { color: 'default', label: 'Игнорирован' },
};

const CommentsList: React.FC = () => {
  const [comments, setComments] = useState<CommentType[]>([]);
  const [rules, setRules] = useState<Record<number, RuleType>>({});
  const [brands, setBrands] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState<Record<string, any>>({});
  const [showReplies, setShowReplies] = useState(true);
  const [replyTarget, setReplyTarget] = useState<CommentType | null>(null);
  const [replyDraft, setReplyDraft] = useState('');
  const [replying, setReplying] = useState(false);

  const openReply = (c: CommentType) => {
    setReplyTarget(c);
    setReplyDraft(c.published_reply || (c.matched_rule_id && rules[c.matched_rule_id]?.reply_template) || '');
  };

  const submitReply = async () => {
    if (!replyTarget || !replyDraft.trim()) return;
    setReplying(true);
    try {
      const res = await apiFetch<{ ok: boolean; detail: string }>(`/comments/${replyTarget.id}/reply`, {
        method: 'POST', body: { text: replyDraft },
      });
      if (res.ok) {
        message.success(res.detail || 'Ответ опубликован');
        setReplyTarget(null);
        fetchComments();
      } else {
        message.error(res.detail || 'Не удалось опубликовать');
      }
    } catch (e: any) {
      message.error(e.message || 'Ошибка публикации');
    }
    setReplying(false);
  };

  const [rechecking, setRechecking] = useState(false);

  const handleRecheck = async () => {
    setRechecking(true);
    try {
      const res = await apiFetch<{ checked: number; updated: number }>('/comments/recheck', { method: 'POST' });
      message.success(`Перепроверено: ${res.checked}, обновлено: ${res.updated}`);
      fetchComments();
    } catch (e: any) {
      message.error(e.message || 'Не удалось перепроверить');
    }
    setRechecking(false);
  };

  const fetchRules = useCallback(async () => {
    try {
      const data = await apiFetch<RuleType[]>('/rules/');
      const map: Record<number, RuleType> = {};
      data.forEach((r) => { map[r.id] = r; });
      setRules(map);
    } catch (e) { /* ignore */ }
  }, []);

  const fetchBrands = useCallback(async () => {
    try {
      const data = await apiFetch<any[]>('/brands/');
      setBrands(data.map((b) => b.name));
    } catch (e) { /* ignore */ }
  }, []);

  const fetchComments = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiFetch<CommentType[]>('/comments/', { params: { ...filters, limit: 0 } });
      setComments(data);
    } catch (error: any) {
      message.error(error.message || 'Не удалось загрузить комментарии');
    }
    setLoading(false);
  }, [filters]);

  useEffect(() => { fetchRules(); fetchBrands(); }, [fetchRules, fetchBrands]);
  useEffect(() => { fetchComments(); }, [fetchComments]);

  const str = (v?: string) => (v || '').toLowerCase();
  const dt = (v?: string) => (v ? new Date(v).getTime() : 0);
  const ruleName = (c: CommentType) => (c.matched_rule_id && rules[c.matched_rule_id]?.name) || '';
  const replyText = (c: CommentType) =>
    c.published_reply || (c.matched_rule_id && rules[c.matched_rule_id]?.reply_template) || '';

  const profileUrl = (c: CommentType): string | null => {
    const u = (c.author || '').replace(/^@/, '');
    if (!u) return null;
    if (c.platform === 'Instagram') return `https://www.instagram.com/${u}/`;
    if (c.platform === 'TikTok') return `https://www.tiktok.com/@${u}`;
    return null;
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 70, sorter: (a: CommentType, b: CommentType) => a.id - b.id },
    { title: 'Бренд', dataIndex: 'brand', key: 'brand', width: 130, ellipsis: true, sorter: (a: CommentType, b: CommentType) => str(a.brand).localeCompare(str(b.brand)) },
    { title: 'Площадка', dataIndex: 'platform', key: 'platform', width: 110, sorter: (a: CommentType, b: CommentType) => str(a.platform).localeCompare(str(b.platform)) },
    {
      title: 'Автор', dataIndex: 'author', key: 'author', width: 160, ellipsis: true,
      sorter: (a: CommentType, b: CommentType) => str(a.author).localeCompare(str(b.author)),
      render: (_: any, c: CommentType) => {
        const url = profileUrl(c);
        if (!c.author) return <span style={{ color: '#aaa' }}>—</span>;
        return url
          ? <a href={url} target="_blank" rel="noopener noreferrer">{c.author}</a>
          : c.author;
      },
    },
    {
      title: 'Тип', key: 'kind', width: 110,
      filters: [{ text: 'Комментарии', value: 'comment' }, { text: 'Ответы', value: 'reply' }],
      onFilter: (value: any, c: CommentType) => (value === 'reply' ? !!c.parent_external_id : !c.parent_external_id),
      render: (_: any, c: CommentType) => (c.parent_external_id ? <Tag color="purple">Ответ</Tag> : <Tag>Комментарий</Tag>),
    },
    {
      title: 'Текст', dataIndex: 'text', key: 'text', width: 320,
      sorter: (a: CommentType, b: CommentType) => str(a.text).localeCompare(str(b.text)),
      render: (v?: string) => (
        <div style={{ maxWidth: 320, whiteSpace: 'normal', wordBreak: 'break-word' }}>{v}</div>
      ),
    },
    {
      title: 'Правило', key: 'rule', width: 160, ellipsis: true,
      sorter: (a: CommentType, b: CommentType) => ruleName(a).localeCompare(ruleName(b)),
      render: (_: any, c: CommentType) =>
        c.matched_rule_id && rules[c.matched_rule_id] ? rules[c.matched_rule_id].name : <span style={{ color: '#aaa' }}>—</span>,
    },
    {
      title: 'Предложенный ответ', key: 'reply', width: 260,
      sorter: (a: CommentType, b: CommentType) => replyText(a).localeCompare(replyText(b)),
      render: (_: any, c: CommentType) => {
        const v = replyText(c);
        return v ? (
          <div title={v} style={{ maxWidth: 240, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{v}</div>
        ) : (
          <span style={{ color: '#aaa' }}>—</span>
        );
      },
    },
    {
      title: 'Статус', dataIndex: 'status', key: 'status', width: 130,
      sorter: (a: CommentType, b: CommentType) => str(a.status).localeCompare(str(b.status)),
      render: (status: string) => {
        const s = statusLabels[status] || { color: 'default', label: status };
        return <Tag color={s.color}>{s.label}</Tag>;
      },
    },
    {
      title: 'Дата комментария', dataIndex: 'comment_date', key: 'comment_date', width: 170,
      defaultSortOrder: 'descend' as const,
      sorter: (a: CommentType, b: CommentType) => dt(a.comment_date) - dt(b.comment_date),
      render: (v?: string) => formatDateTime(v),
    },
    {
      title: 'Получен', dataIndex: 'created_at', key: 'created_at', width: 170,
      sorter: (a: CommentType, b: CommentType) => dt(a.created_at) - dt(b.created_at),
      render: (v?: string) => formatDateTime(v),
    },
    {
      title: 'Обработан', dataIndex: 'processed_at', key: 'processed_at', width: 170,
      sorter: (a: CommentType, b: CommentType) => dt(a.processed_at) - dt(b.processed_at),
      render: (v?: string) => formatDateTime(v),
    },
    {
      title: 'Ссылка на пост', dataIndex: 'comment_url', key: 'comment_url', width: 130,
      render: (v?: string) =>
        v ? <a href={v} target="_blank" rel="noopener noreferrer">Открыть</a> : <span style={{ color: '#aaa' }}>—</span>,
    },
    {
      title: 'В Telegram', dataIndex: 'sent_to_telegram', key: 'sent_to_telegram', width: 120,
      sorter: (a: CommentType, b: CommentType) => Number(!!a.sent_to_telegram) - Number(!!b.sent_to_telegram),
      render: (v?: boolean) => (v ? <Tag color="green">отправлен</Tag> : <Tag color="default">—</Tag>),
    },
    {
      title: 'Подтвердил', key: 'published_by', width: 200,
      render: (_: any, c: CommentType) =>
        c.published_reply ? (
          <span title={c.published_at ? formatDateTime(c.published_at) : ''}>
            <Tag color="green">опубликован</Tag>{c.published_by || ''}
          </span>
        ) : (
          <span style={{ color: '#ccc' }}>—</span>
        ),
    },
    {
      title: 'Ответ', key: 'reply_action', width: 120,
      render: (_: any, c: CommentType) => (
        <Button size="small" icon={<SendOutlined />} onClick={() => openReply(c)}>
          Ответить
        </Button>
      ),
    },
  ];

  const setFilter = (key: string, value: any) => setFilters((f) => ({ ...f, [key]: value }));

  // Ответы группируем по родителю — для раскрытия ветки под комментарием.
  // При этом все комментарии (включая ответы) показываются и как строки списка.
  const repliesByParent: Record<string, CommentType[]> = {};
  comments.forEach((c) => {
    if (c.parent_external_id) {
      (repliesByParent[c.parent_external_id] = repliesByParent[c.parent_external_id] || []).push(c);
    }
  });

  // По переключателю показываем ответы как строки списка или скрываем (оставляя только комментарии).
  const visibleComments = showReplies ? comments : comments.filter((c) => !c.parent_external_id);

  const replyColumns = [
    { title: 'Автор', dataIndex: 'author', key: 'author', width: 160 },
    { title: 'Ответ', dataIndex: 'text', key: 'text', render: (v?: string) => <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{v}</div> },
    {
      title: 'Правило', key: 'rule', width: 160,
      render: (_: any, c: CommentType) => (c.matched_rule_id && rules[c.matched_rule_id] ? rules[c.matched_rule_id].name : '—'),
    },
    {
      title: 'Статус', dataIndex: 'status', key: 'status', width: 130,
      render: (status: string) => {
        const s = statusLabels[status] || { color: 'default', label: status };
        return <Tag color={s.color}>{s.label}</Tag>;
      },
    },
    {
      title: 'В Telegram', dataIndex: 'sent_to_telegram', key: 'sent', width: 110,
      render: (v?: boolean) => (v ? <Tag color="green">отправлен</Tag> : <Tag color="default">—</Tag>),
    },
  ];

  return (
    <>
      <Space wrap style={{ marginBottom: 16 }}>
        <Select allowClear placeholder="Бренд" style={{ width: 160 }}
          onChange={(v) => setFilter('brand', v)}
          options={brands.map((b) => ({ value: b }))} />
        <Select allowClear placeholder="Площадка" style={{ width: 140 }}
          onChange={(v) => setFilter('platform', v)}
          options={[{ value: 'Instagram' }, { value: 'TikTok' }]} />
        <Select allowClear placeholder="Статус" style={{ width: 150 }}
          onChange={(v) => setFilter('status', v)}
          options={[
            { value: 'new', label: 'Новый' },
            { value: 'processed', label: 'Обработан' },
            { value: 'published', label: 'Опубликован' },
            { value: 'ignored', label: 'Игнорирован' },
          ]} />
        <Select allowClear showSearch optionFilterProp="label" placeholder="Правило" style={{ width: 200 }}
          onChange={(v) => setFilter('rule_id', v)}
          options={Object.values(rules).map((r) => ({ value: r.id, label: r.name }))} />
        <Input allowClear placeholder="Автор" style={{ width: 150 }}
          onChange={(e) => setFilter('author', e.target.value)} />
        <Input.Search allowClear placeholder="Поиск по тексту" style={{ width: 240 }}
          onSearch={(v) => setFilter('text', v)}
          onChange={(e) => { if (!e.target.value) setFilter('text', undefined); }} />
        <DatePicker.RangePicker
          onChange={(_, ds) => { setFilter('date_from', ds?.[0]); setFilter('date_to', ds?.[1]); }} />
        <Switch
          checked={showReplies}
          onChange={setShowReplies}
          checkedChildren="С ответами"
          unCheckedChildren="Без ответов"
        />
        <Button icon={<ReloadOutlined />} onClick={fetchComments}>Обновить</Button>
        <Button icon={<CheckCircleOutlined />} loading={rechecking} onClick={handleRecheck}
          title="Прогнать необработанные комментарии через текущие правила">
          Перепроверить правила
        </Button>
      </Space>
      <Table
        columns={columns}
        dataSource={visibleComments}
        rowKey="id"
        loading={loading}
        scroll={{ x: 'max-content' }}
        pagination={{
          defaultPageSize: 50,
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50', '100', '200', '500'],
          showTotal: (total: number) => `Всего: ${total}`,
        }}
        rowClassName={(c) =>
          c.matched_rule_id && rules[c.matched_rule_id]?.rule_type === 'negative' ? 'negative-row' : ''
        }
        expandable={{
          rowExpandable: (c) => (repliesByParent[c.external_id]?.length || 0) > 0,
          expandedRowRender: (c) => (
            <Table
              columns={replyColumns}
              dataSource={repliesByParent[c.external_id] || []}
              rowKey="id"
              size="small"
              pagination={false}
              rowClassName={(r) =>
                r.matched_rule_id && rules[r.matched_rule_id]?.rule_type === 'negative' ? 'negative-row' : ''
              }
            />
          ),
        }}
      />
      <Modal
        title={replyTarget ? `Ответить — ${replyTarget.brand} / @${replyTarget.author}` : 'Ответить'}
        open={replyTarget !== null}
        onCancel={() => setReplyTarget(null)}
        onOk={submitReply}
        okText="Опубликовать"
        confirmLoading={replying}
        okButtonProps={{ disabled: !replyDraft.trim() }}
      >
        {replyTarget && (
          <>
            <div style={{ marginBottom: 8, color: '#888' }}>
              Комментарий: <i>{replyTarget.text}</i>
            </div>
            <Input.TextArea
              rows={4}
              value={replyDraft}
              onChange={(e) => setReplyDraft(e.target.value)}
              placeholder="Текст ответа — будет опубликован в соцсети через токены бренда"
            />
            <div style={{ marginTop: 8, color: '#888', fontSize: 12 }}>
              Ответ уйдёт в {replyTarget.platform} под комментарий от имени бренда «{replyTarget.brand}».
            </div>
          </>
        )}
      </Modal>
    </>
  );
};

export default CommentsList;
